<template>
    <div id="voucher-validation-container" style="display: contents">
        <CustomDialog
            v-model="showModal"
            :defaultTitle="isUndatedTicket ? $t('checkout.voucher.validate_undated') : $t('checkout.voucher.validate_gift')"
            width="600px"
        >
            <template #content>
                <div class="ppv2-p-6">
                    <!-- Recipient Information -->
                    <div class="ppv2-mb-6">
                        <BaseRadioGroup
                            v-model="recipientType"
                            :options="recipientTypeOptions"
                            :label="$t('checkout.voucher.beneficiary')"
                            @input="onRecipientTypeChange"
                        />

                        <!-- Nested options for "Another Customer" -->
                        <div
                            v-if="recipientType === 'another'"
                            class="ppv2-ml-4 ppv2-pl-4 ppv2-border-l-2 ppv2-border-gray-200 ppv2-mt-4"
                        >
                            <BaseRadioGroup
                                v-model="anotherCustomerType"
                                :options="anotherCustomerTypeOptions"
                                :label="''"
                                @input="onAnotherCustomerTypeChange"
                            />

                            <!-- Search Customer Fields -->
                            <div v-show="anotherCustomerType === 'search'" class="ppv2-ml-6 ppv2-mt-4 ppv2-mb-3">
                                <select
                                    v-model="formData.recipientSelect"
                                    id="recipientSelect"
                                    style="width: 100%"
                                    class="form-select"
                                >
                                    <option value="">{{ $t('checkout.voucher.choose_customer') }}</option>
                                    <option v-for="customer in customers" :key="customer.id" :value="customer.id">
                                        {{ formatCustomerDisplay(customer) }}
                                    </option>
                                </select>
                                <!-- Display selected customer -->
                                <div
                                    v-if="selectedCustomer"
                                    class="ppv2-mt-2 ppv2-p-3 ppv2-bg-blue-50 ppv2-rounded-lg ppv2-text-sm"
                                >
                                    <strong class="ppv2-text-blue-900"
                                        >{{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }}</strong
                                    >
                                    <span v-if="selectedCustomer.email" class="ppv2-text-blue-700 ppv2-ml-1"
                                        >({{ selectedCustomer.email }})</span
                                    >
                                </div>
                            </div>

                            <!-- New Beneficiary Fields -->
                            <div v-show="anotherCustomerType === 'new'" class="ppv2-ml-6 ppv2-mt-4 ppv2-mb-3">
                                <div class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-4 ppv2-mb-3">
                                    <CustomInput
                                        :label="$t('checkout.voucher.first_name')"
                                        v-model="formData.to_first_name"
                                        type="text"
                                        :required="true"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="14"
                                        class-input-add="ppv2-min-h-[40px]"
                                    />
                                    <CustomInput
                                        :label="$t('checkout.voucher.last_name')"
                                        v-model="formData.to_last_name"
                                        type="text"
                                        :required="true"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="14"
                                        class-input-add="ppv2-min-h-[40px]"
                                    />
                                </div>
                                <div class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-4">
                                    <CustomInput
                                        :label="$t('checkout.voucher.email_beneficiary')"
                                        v-model="formData.to_email"
                                        type="email"
                                        :required="true"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="14"
                                        class-input-add="ppv2-min-h-[40px]"
                                    />
                                    <CustomInput
                                        :label="$t('checkout.voucher.phone_beneficiary')"
                                        v-model="formData.to_phone"
                                        type="text"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="14"
                                        class-input-add="ppv2-min-h-[40px]"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Email Section -->
                    <div v-if="!formData.no_receiver" class="ppv2-border-t ppv2-border-gray-200 ppv2-pt-6">
                        <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mb-4">
                            <h4 class="ppv2-text-base poppinsSemiBold poppinsMedium ppv2-m-0">
                                {{ $t('checkout.voucher.send_by_email') }}
                            </h4>
                            <BaseCheckbox v-model="formData.no_mail" :hasBorderChecked="true" size="md" />
                        </div>

                        <!-- Email Details -->
                        <div v-if="formData.no_mail" class="ppv2-flex ppv2-flex-col ppv2-gap-4">
                            <BaseDateInput
                                :label="$t('checkout.voucher.send_date_email')"
                                v-model="formData.send_date"
                                :placeholder="$t('checkout.voucher.send_date_email')"
                                :customSizeLabel="14"
                                customLabelClass="poppinsMedium ppv2-text-black"
                                input-class="!ppv2-min-h-[40px]"
                            />

                            <BaseTextarea
                                :label="$t('checkout.voucher.custom_message')"
                                :placeholder="$t('checkout.voucher.custom_message_placeholder')"
                                v-model="formData.from_message"
                                :max-chars="255"
                                :customSizeLabel="14"
                                customLabelClass="poppinsMedium ppv2-text-black"
                            />
                        </div>
                    </div>
                </div>
            </template>
            <template #actions>
                <div class="ppv2-flex ppv2-justify-center ppv2-w-full" style="gap: 16px">
                    <BaseButton variant="secondary" @click="closeModal" customClass="ppv2-px-8">
                        {{ $t('common.close') }}
                    </BaseButton>
                    <BaseButton
                        variant="primary"
                        :disabled="isSubmitting || !isFormValid"
                        @click="submitForm"
                        :loading="isSubmitting"
                        customClass="ppv2-px-8"
                    >
                        {{ isSubmitting ? $t('checkout.devis.validating') : $t('common.validate') }}
                    </BaseButton>
                </div>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import CustomDialog from '../Shared/CustomDialog.vue'
import CustomInput from '../Inputs/CustomInput.vue'
import BaseDateInput from '../Shared/BaseDateInput.vue'
import BaseTextarea from '../Shared/BaseTextarea.vue'
import BaseCheckbox from '../Shared/BaseCheckbox.vue'
import BaseButton from '../Shared/BaseButton.vue'
import BaseRadioGroup from '../Shared/BaseRadioGroup.vue'

export default {
    name: 'VoucherValidation',
    components: {
        CustomDialog,
        CustomInput,
        BaseDateInput,
        BaseTextarea,
        BaseCheckbox,
        BaseButton,
        BaseRadioGroup,
    },
    props: {
        currentCustomer: {
            type: Object,
            default: () => ({}),
        },
        customers: {
            type: Array,
            default: () => [],
        },
        draft: {
            type: Object,
            default: null,
        },
        orderId: {
            type: [String, Number],
            default: null,
        },
        csrfToken: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showModal: false,
            recipientType: 'current',
            anotherCustomerType: 'search',
            formData: {
                from_first_name: '',
                from_last_name: '',
                from_email: '',
                from_phone_number: '',
                recipientSelect: '',
                new_customer: false,
                no_receiver: false,
                to_first_name: '',
                to_last_name: '',
                to_email: '',
                to_phone: '',
                no_mail: false,
                send_date: this.getTodayDate(),
                from_message: '',
                type: 'App\\Models\\Voucher\\Voucher',
                order_id: '',
            },
            isSubmitting: false,
            select2Instance: null,
            selectedCustomer: null,
        }
    },
    computed: {
        isUndatedTicket() {
            return this.draft?.itemable?.items?.[0]?.item_type === 'App\\Models\\Prestation\\Prestation'
        },
        recipientTypeOptions() {
            const options = []
            if (this.currentCustomer && !this.currentCustomer.is_guest) {
                options.push({
                    value: 'current',
                    label: this.$t('checkout.voucher.this_customer'),
                    description: `(${this.currentCustomer.first_name} ${this.currentCustomer.last_name}${
                        this.currentCustomer.email ? ' - ' + this.currentCustomer.email : ''
                    })`,
                })
            }
            options.push({
                value: 'another',
                label: this.$t('checkout.voucher.another_customer'),
            })
            options.push({
                value: 'none',
                label: this.$t('checkout.voucher.no_beneficiary'),
            })
            return options
        },
        anotherCustomerTypeOptions() {
            return [
                {
                    value: 'search',
                    label: this.$t('checkout.voucher.search_customer'),
                },
                {
                    value: 'new',
                    label: this.$t('checkout.voucher.new_beneficiary'),
                },
            ]
        },
        isFormValid() {
            // If "Pas de bénéficiaire" is selected, form is valid
            if (this.recipientType === 'none') {
                return true
            }

            // If "Ce client" is selected, form is valid
            if (this.recipientType === 'current') {
                return true
            }

            // If "À un autre client" is selected
            if (this.recipientType === 'another') {
                // If searching for a customer, one must be selected
                if (this.anotherCustomerType === 'search') {
                    return !!this.formData.recipientSelect
                }

                // If creating a new beneficiary, all fields are required and email must be valid
                if (this.anotherCustomerType === 'new') {
                    return !!(
                        this.formData.to_first_name &&
                        this.formData.to_last_name &&
                        this.formData.to_email &&
                        this.isValidEmail(this.formData.to_email)
                    )
                }
            }

            return false
        },
    },
    watch: {
        recipientType(newVal) {
            // When switching to "another", default to search
            if (newVal === 'another') {
                this.anotherCustomerType = 'search'
                this.$nextTick(() => {
                    this.initializeSelect2()
                })
            }
        },
    },
    mounted() {
        this.initializeForm()
        this.initializeSelect2()
        this.setupModalEvents()
    },
    beforeDestroy() {
        if (this.select2Instance) {
            $(this.select2Instance).select2('destroy')
        }
        $(document).off('click', '[data-bs-target="#modal-voucher"]')
        const dummy = document.getElementById('modal-voucher')
        if (dummy) {
            $(dummy).off('show.bs.modal')
            dummy.remove()
        }
    },
    methods: {
        initializeForm() {
            this.formData.from_first_name = this.currentCustomer?.first_name || ''
            this.formData.from_last_name = this.currentCustomer?.last_name || ''
            this.formData.from_email = this.currentCustomer?.email || ''
            this.formData.from_phone_number = this.currentCustomer?.phone_number || ''
            this.formData.order_id = this.orderId || ''

            // Auto-select current customer if not a guest, otherwise select "Pas de bénéficiaire"
            if (this.currentCustomer && !this.currentCustomer.is_guest && this.currentCustomer.id) {
                this.recipientType = 'current'
                this.formData.recipientSelect = this.currentCustomer.id
                this.selectedCustomer = {
                    id: this.currentCustomer.id,
                    first_name: this.currentCustomer.first_name || '',
                    last_name: this.currentCustomer.last_name || '',
                    email: this.currentCustomer.email || '',
                }
            } else {
                // For guest users, default to "Pas de bénéficiaire"
                this.recipientType = 'none'
                this.formData.no_receiver = true
            }
        },
        initializeSelect2() {
            const self = this
            this.$nextTick(() => {
                const $select = $('#recipientSelect')
                if (!$select.length) return

                if (this.select2Instance) {
                    try {
                        $(this.select2Instance).select2('destroy')
                    } catch (e) {}
                }

                $select.select2({
                    ajax: {
                        url: this.getRoute('getCustomerEmailByName'),
                        data: function (params) {
                            return {
                                search: params.term,
                                page: params.page || 1,
                                type: 'public',
                                getWithGuestUsers: false,
                            }
                        },
                        processResults: function (data, params) {
                            params.page = params.page || 1
                            const results = data.data.map((element) => ({
                                id: element.id,
                                text: element.email
                                    ? `${element.first_name} ${element.last_name} - ${element.email}`
                                    : `${element.first_name} ${element.last_name} - ${self.$t('checkout.voucher.no_email')}`,
                            }))
                            return {
                                results: results,
                                pagination: {
                                    more: params.page * 1 < data.count_filtered,
                                },
                            }
                        },
                    },
                    dropdownParent: $select.parent(),
                    placeholder: self.$t('checkout.voucher.choose_customer'),
                })

                $select.on('change', function () {
                    const selectedId = $(this).val()
                    self.formData.recipientSelect = selectedId

                    // Store selected customer data
                    if (selectedId) {
                        const selectedData = $select.select2('data')[0]
                        if (selectedData && selectedData.id) {
                            // Extract customer info from the text
                            const text = selectedData.text
                            const parts = text.split(' - ')
                            if (parts.length >= 2) {
                                const nameParts = parts[0].trim().split(' ')
                                self.selectedCustomer = {
                                    id: selectedData.id,
                                    first_name: nameParts[0] || '',
                                    last_name: nameParts.slice(1).join(' ') || '',
                                    email: parts[1] || '',
                                }
                            }
                        }
                    } else {
                        self.selectedCustomer = null
                    }
                })

                this.select2Instance = $select
            })
        },
        setupModalEvents() {
            const self = this

            // Intercept clicks on any element with data-bs-target="#modal-voucher"
            $(document).on('click', '[data-bs-target="#modal-voucher"]', function (e) {
                e.preventDefault()
                const orderId = $(this).attr('data-order') || $(this).attr('data-order-item-id')
                if (orderId) {
                    self.formData.order_id = orderId
                }
                self.showModal = true
            })

            // Intercept bootstrap modal show if triggered programmatically
            const dummy = document.createElement('div')
            dummy.id = 'modal-voucher'
            dummy.style.display = 'none'
            document.body.appendChild(dummy)

            $(dummy).on('show.bs.modal', function (e) {
                const button = e.relatedTarget
                if (button) {
                    const orderId = button.getAttribute('data-order') || button.getAttribute('data-order-item-id')
                    if (orderId) {
                        self.formData.order_id = orderId
                    }
                }
                self.showModal = true
                e.preventDefault()
                return false
            })
        },
        onRecipientTypeChange() {
            // Update formData flags based on recipientType
            this.formData.no_receiver = this.recipientType === 'none'
            this.formData.new_customer = false

            // Clear fields when switching types
            if (this.recipientType !== 'another') {
                this.formData.recipientSelect = ''
                this.selectedCustomer = null
                this.formData.to_first_name = ''
                this.formData.to_last_name = ''
                this.formData.to_email = ''
                this.formData.to_phone = ''
            }

            // If switching to current customer, set recipientSelect to current customer
            if (this.recipientType === 'current' && this.currentCustomer && !this.currentCustomer.is_guest) {
                this.formData.recipientSelect = this.currentCustomer.id
                this.selectedCustomer = {
                    id: this.currentCustomer.id,
                    first_name: this.currentCustomer.first_name || '',
                    last_name: this.currentCustomer.last_name || '',
                    email: this.currentCustomer.email || '',
                }
            }
        },
        onAnotherCustomerTypeChange() {
            // Update formData.new_customer based on anotherCustomerType
            this.formData.new_customer = this.anotherCustomerType === 'new'

            // Clear fields when switching between search and new
            if (this.anotherCustomerType === 'search') {
                this.formData.to_first_name = ''
                this.formData.to_last_name = ''
                this.formData.to_email = ''
                this.formData.to_phone = ''
            } else {
                this.formData.recipientSelect = ''
                this.selectedCustomer = null
            }
        },
        formatCustomerDisplay(customer) {
            if (customer.is_guest || !customer.email) {
                return `${customer.first_name} ${customer.last_name || ''} - ${this.$t('checkout.voucher.no_email')}`
            }
            return `${customer.first_name} ${customer.last_name || ''} - ${customer.email}`
        },
        getTodayDate() {
            const today = new Date()
            const year = today.getFullYear()
            const month = String(today.getMonth() + 1).padStart(2, '0')
            const day = String(today.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },
        isValidEmail(email) {
            if (!email) return false
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(email)
        },
        getRoute(routeName) {
            const routes = {
                getCustomerEmailByName: '/back-office/get/customer_by_name',
                voucherValidate: '/back-office/caisse/voucher/validate',
            }
            return routes[routeName] || '#'
        },
        async submitForm() {
            if (this.isSubmitting) return

            this.isSubmitting = true

            try {
                const formDataToSend = this.buildFormData()

                const response = await fetch(this.getRoute('voucherValidate'), {
                    method: 'POST',
                    body: formDataToSend,
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })

                const result = await response.json()

                if (response.ok && result.success) {
                    window.location.reload()
                } else {
                    this.$toast.error(result.message)
                }
            } catch (error) {
                this.$toast.error(error?.response?.data?.message || error?.message || 'Error occurred')
            } finally {
                this.isSubmitting = false
            }
        },
        buildFormData() {
            const formData = new FormData()

            // Base fields
            const baseFields = {
                _token: this.csrfToken,
                type: this.formData.type,
                isUndatedTicket: this.isUndatedTicket,
                from_first_name: this.formData.from_first_name,
                from_last_name: this.formData.from_last_name,
                from_email: this.formData.from_email,
                from_phone_number: this.formData.from_phone_number,
                send_date: this.formData.send_date,
                from_message: this.formData.from_message,
                order_id: this.formData.order_id,
            }

            Object.entries(baseFields).forEach(([key, value]) => {
                formData.append(key, value)
            })

            // Recipient fields based on type
            this.appendRecipientData(formData)

            // Email option
            if (this.formData.no_mail) {
                formData.append('no_mail', 'on')
            }

            return formData
        },
        appendRecipientData(formData) {
            if (this.recipientType === 'none') {
                formData.append('no_receiver', 'on')
                return
            }

            if (this.recipientType === 'current') {
                formData.append('recipientSelect', this.formData.recipientSelect)
                return
            }

            if (this.recipientType === 'another') {
                if (this.anotherCustomerType === 'new') {
                    formData.append('new_customer', 'on')
                    formData.append('to_first_name', this.formData.to_first_name)
                    formData.append('to_last_name', this.formData.to_last_name)
                    formData.append('to_email', this.formData.to_email)
                    formData.append('to_phone', this.formData.to_phone)
                } else {
                    formData.append('recipientSelect', this.formData.recipientSelect)
                }
            }
        },
        closeModal() {
            this.showModal = false
        },
        setOrderItemId(orderItemId) {
            this.formData.order_item = orderItemId
        },
    },
}
</script>

<style scoped>
.form-check-input[type='radio'] {
    cursor: pointer;
    width: 18px;
    height: 18px;
    margin-top: 0.15em;
}

.form-check-input[type='radio']:checked {
    background-color: #1e40af;
    border-color: #1e40af;
}

.form-check-label {
    cursor: pointer;
    margin-left: 0.5rem;
}
</style>
